/** Client-side mirrors of backend/validation.py so errors appear inline,
 *  instantly, before anything is sent. The server re-validates regardless. */

const EMAIL_RE =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;
const SA_PHONE_RE = /^(?:\+?27|0)(6|7|8|1|2|3|4|5)\d{8}$/;
const LETTERS_RE = /[A-Za-z\u00C0-\u024F]/g;
const DIGITS_RE = /\d/;
/** A person's name: letters (incl. accents), spaces and the usual punctuation. */
const NAME_ALLOWED_RE = /^[A-Za-z\u00C0-\u024F\s'\u2019\-.]+$/;
/** A South African number has 9 digits after the leading 0 / +27. */
const SA_LOCAL_DIGITS = 9;

export const PHONE_MAX_CHARS = 12; // "081 788 2993" — 10 digits + 2 spaces
export const NAME_MAX_CHARS = 120;
export const COMPANY_MAX_CHARS = 160;

export function normalisePhone(raw) {
  return String(raw || "").replace(/[\s\-().]/g, "");
}

/**
 * Format a South African number as the customer types: "081 788 2993".
 *
 * A local (0-prefixed) number is grouped 3-3-4, which is exactly 12 characters
 * including the two spaces. A +27 number is left unspaced because
 * "+27817882993" is itself exactly 12 characters.
 */
export function formatPhoneAsTyped(raw) {
  const value = String(raw || "");

  if (value.trim().startsWith("+")) {
    // Keep the leading + and digits only, capped at 12 characters.
    const digits = value.replace(/[^\d]/g, "").slice(0, 11);
    return `+${digits}`;
  }

  const digits = value.replace(/[^\d]/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

/** Drop anything a person's name can't contain — used as the field is typed. */
export function stripDigitsForName(raw) {
  return String(raw || "")
    .replace(/[^A-Za-z\u00C0-\u024F\s'\u2019\-.]/g, "")
    .slice(0, NAME_MAX_CHARS);
}

export function validateEmail(raw) {
  const value = String(raw || "").trim();
  if (!value) return "Please enter your email address.";
  if (value.length > 254 || !EMAIL_RE.test(value)) {
    return "That doesn't look like a valid email address \u2014 please check it (e.g. name@company.co.za).";
  }
  return null;
}

/**
 * South African numbers only, with explicit minimum / maximum length.
 * Accepted: 0XXXXXXXXX (10 digits) or +27XXXXXXXXX / 27XXXXXXXXX.
 */
export function validatePhone(raw) {
  const value = normalisePhone(raw);
  if (!value) return "Please enter your phone number.";
  if (!/^\+?\d+$/.test(value)) {
    return "Please use digits only \u2014 you can add the +27 country code and spaces if you like.";
  }

  const digits = value.startsWith("+") ? value.slice(1) : value;
  let local = null;
  if (digits.startsWith("27")) local = digits.slice(2);
  else if (digits.startsWith("0")) local = digits.slice(1);
  else {
    return "South African numbers start with 0 (e.g. 081 788 2993) or +27 (e.g. +27 81 788 2993).";
  }

  if (local.length < SA_LOCAL_DIGITS) {
    const shortBy = SA_LOCAL_DIGITS - local.length;
    return `That number is ${shortBy} digit${shortBy === 1 ? "" : "s"} too short \u2014 a South African number has 10 digits, like 081 788 2993.`;
  }
  if (local.length > SA_LOCAL_DIGITS) {
    const longBy = local.length - SA_LOCAL_DIGITS;
    return `That number is ${longBy} digit${longBy === 1 ? "" : "s"} too long \u2014 a South African number has 10 digits, like 081 788 2993.`;
  }
  if (!SA_PHONE_RE.test(value)) {
    return "Please enter a valid South African number \u2014 10 digits starting with 0 (e.g. 081 788 2993) or the +27 format.";
  }
  return null;
}

/** A person's name: letters only. Digits are rejected outright. */
export function validateName(raw, fieldLabel = "your full name") {
  const value = String(raw || "").trim();
  if (!value) return `Please enter ${fieldLabel}.`;
  if (DIGITS_RE.test(value)) {
    return `Please enter ${fieldLabel} using letters only \u2014 no numbers.`;
  }
  if (!NAME_ALLOWED_RE.test(value)) {
    return `Please enter ${fieldLabel} using letters only \u2014 spaces, hyphens and apostrophes are fine.`;
  }
  if (value.length < 2) return `Please enter ${fieldLabel} in full.`;
  const letters = value.match(LETTERS_RE) || [];
  if (letters.length < 2) return `Please enter ${fieldLabel} in full.`;
  if (value.length > NAME_MAX_CHARS) {
    return `Please shorten ${fieldLabel} to ${NAME_MAX_CHARS} characters or fewer.`;
  }
  return null;
}

/** A business name may contain letters, numbers or both. */
export function validateCompany(raw) {
  const value = String(raw || "").trim();
  if (!value) return "Please enter your company or business name.";
  if (value.length < 2) return "Please enter your company or business name in full.";
  if (value.length > COMPANY_MAX_CHARS) {
    return `Please shorten your company name to ${COMPANY_MAX_CHARS} characters or fewer.`;
  }
  return null;
}

/** Shared by the contact gate and the fallback form. */
export function validateContactForm(values, { requireConsent = true } = {}) {
  const errors = {};
  const name = validateName(values.full_name);
  if (name) errors.full_name = name;
  const email = validateEmail(values.email);
  if (email) errors.email = email;
  const phone = validatePhone(values.phone);
  if (phone) errors.phone = phone;
  const company = validateCompany(values.company);
  if (company) errors.company = company;
  if (requireConsent && !values.consent) {
    errors.consent =
      "Please tick the box to confirm you're happy for us to use these details to prepare and discuss your quote.";
  }
  return errors;
}
